/**
 * PHARMA ANALYTICS VIEW
 * Longitudinal analysis, protocol-level toxicity profiles, 
 * time-on-treatment, EIG rates, and study report generation.
 */

import { useState, useEffect, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import ToxicityByCycleDay from "./ToxicityByCycleDay";
import {
  TrendingUp, FileText, Download, Loader2, Search, RefreshCw,
  Pill, Activity, AlertTriangle, Clock, Users,
  BarChart3, ChevronDown, ChevronUp, CheckSquare, MessageSquare
} from "lucide-react";

export default function PharmaAnalyticsView() {
  const [protocolData, setProtocolData] = useState(null);
  const [studyReport, setStudyReport] = useState(null);
  const [adherenceData, setAdherenceData] = useState(null);
  const [transmissionData, setTransmissionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingAdherence, setLoadingAdherence] = useState(false);
  const [loadingTransmissions, setLoadingTransmissions] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState("protocols");
  const [expandedProto, setExpandedProto] = useState(null);

  // Study report filters
  const [reportCancer, setReportCancer] = useState("");
  const [reportProtocol, setReportProtocol] = useState("");
  const [reportMonths, setReportMonths] = useState(6);

  const fetchProtocolAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/pharma/protocol-analytics");
      setProtocolData(res.data);
    } catch {
      toast.error("Erreur chargement analytics protocole");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProtocolAnalytics();
  }, [fetchProtocolAnalytics]);

  const fetchAdherence = async () => {
    setLoadingAdherence(true);
    try {
      const res = await api.get("/pharma/adherence?months=6");
      setAdherenceData(res.data);
    } catch {
      toast.error("Erreur chargement adherence");
    } finally {
      setLoadingAdherence(false);
    }
  };

  useEffect(() => {
    if (activeTab === "adherence" && !adherenceData) {
      fetchAdherence();
    }
  }, [activeTab, adherenceData]);

  const fetchTransmissions = async () => {
    setLoadingTransmissions(true);
    try {
      const res = await api.get("/pharma/transmission-analytics?months=6");
      setTransmissionData(res.data);
    } catch {
      toast.error("Erreur chargement transmissions structurées");
    } finally {
      setLoadingTransmissions(false);
    }
  };

  useEffect(() => {
    if (activeTab === "transmissions" && !transmissionData) {
      fetchTransmissions();
    }
  }, [activeTab, transmissionData]);

  const fetchStudyReport = async () => {
    setLoadingReport(true);
    try {
      const params = new URLSearchParams();
      if (reportCancer) params.append("cancer_type", reportCancer);
      if (reportProtocol) params.append("protocol", reportProtocol);
      params.append("months", reportMonths);
      const res = await api.get(`/pharma/study-report?${params.toString()}`);
      setStudyReport(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur generation rapport");
    } finally {
      setLoadingReport(false);
    }
  };

  const downloadStudyPdf = async () => {
    setExportingPdf(true);
    try {
      const params = new URLSearchParams();
      if (reportCancer) params.append("cancer_type", reportCancer);
      if (reportProtocol) params.append("protocol", reportProtocol);
      params.append("months", reportMonths);
      const res = await api.get(`/pharma/study-report/pdf?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `HANA_Rapport_Etude_${reportCancer || "global"}_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rapport PDF telecharge");
    } catch {
      toast.error("Erreur export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-6 mt-4" data-testid="pharma-analytics-view">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Pharma Analytics</h2>
          <p className="text-xs text-slate-500">Profils de toxicite, temps sous traitement, rapports d'etude</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {[
          { id: "protocols", label: "Protocoles", icon: Pill },
          { id: "adherence", label: "Observance", icon: CheckSquare },
          { id: "transmissions", label: "Transmissions", icon: MessageSquare },
          { id: "toxicity", label: "Toxicité / cure", icon: Activity },
          { id: "report", label: "Rapport d'etude", icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`pharma-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Protocol Analytics */}
      {activeTab === "protocols" && (
        <div className="space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          )}

          {protocolData && !loading && (
            <>
              {/* Summary stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="protocol-stats">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-indigo-500" />
                    <span className="text-xs text-slate-500">Patients</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">{protocolData.total_patients}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Pill className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-slate-500">Protocoles</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">{protocolData.total_protocols}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-teal-500" />
                    <span className="text-xs text-slate-500">Genere le</span>
                  </div>
                  <p className="text-sm font-medium text-slate-600">{new Date(protocolData.generated_at).toLocaleDateString("fr-FR")}</p>
                </div>
              </div>

              {/* Protocol cards */}
              <div className="space-y-3" data-testid="protocol-list">
                {protocolData.protocols?.map((proto, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`protocol-card-${idx}`}>
                    <button
                      onClick={() => setExpandedProto(expandedProto === idx ? null : idx)}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Pill className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-bold text-slate-800">{proto.protocol}</p>
                          <p className="text-xs text-slate-400">{proto.n_patients} patients, {proto.n_checkins} check-ins</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="text-xs text-slate-400">Duree mediane</span>
                          <p className="text-sm font-bold text-slate-700">{proto.median_months_on_treatment} mois</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-slate-400">EIG/100PM</span>
                          <p className={`text-sm font-bold ${proto.eig_rate_per_100pm > 10 ? "text-red-600" : proto.eig_rate_per_100pm > 5 ? "text-orange-500" : "text-emerald-600"}`}>
                            {proto.eig_rate_per_100pm}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-slate-400">% critique</span>
                          <p className={`text-sm font-bold ${proto.pct_critical > 10 ? "text-red-600" : "text-slate-700"}`}>
                            {proto.pct_critical}%
                          </p>
                        </div>
                        {expandedProto === idx ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {expandedProto === idx && (
                      <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                        {/* Alert distribution */}
                        <div className="flex gap-3 mb-3">
                          {["VERT", "ORANGE", "ROUGE"].map(level => {
                            const count = proto.alert_distribution?.[level] || 0;
                            const colors = { VERT: "bg-emerald-100 text-emerald-700", ORANGE: "bg-orange-100 text-orange-700", ROUGE: "bg-red-100 text-red-700" };
                            return (
                              <span key={level} className={`px-3 py-1 rounded-full text-xs font-bold ${colors[level]}`}>
                                {level}: {count}
                              </span>
                            );
                          })}
                        </div>

                        {/* Top toxicities */}
                        {proto.top3_toxicities?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-slate-500 mb-1">Top toxicites (grade 2+)</p>
                            <div className="flex gap-2 flex-wrap">
                              {proto.top3_toxicities.map(t => (
                                <span key={t} className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-700 font-medium">{t}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Toxicity table */}
                        {Object.keys(proto.toxicity_profile || {}).length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b">
                                  <th className="text-left px-3 py-2 font-medium text-slate-600">Symptome</th>
                                  <th className="text-center px-3 py-2 font-medium text-slate-600">Score moy</th>
                                  <th className="text-center px-3 py-2 font-medium text-slate-600">% severe</th>
                                  <th className="text-center px-3 py-2 font-medium text-slate-600">N obs</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(proto.toxicity_profile).slice(0, 8).map(([sym, data]) => (
                                  <tr key={sym} className="border-b border-slate-50">
                                    <td className="px-3 py-1.5 font-medium text-slate-700">{sym}</td>
                                    <td className="px-3 py-1.5 text-center">{data.mean_score}</td>
                                    <td className="px-3 py-1.5 text-center">
                                      <span className={`font-bold ${data.pct_severe > 30 ? "text-red-600" : data.pct_severe > 15 ? "text-orange-500" : "text-slate-600"}`}>
                                        {data.pct_severe}%
                                      </span>
                                    </td>
                                    <td className="px-3 py-1.5 text-center text-slate-400">{data.n_observations}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Adherence Tab */}
      {activeTab === "adherence" && (
        <div className="space-y-4" data-testid="adherence-tab">
          {loadingAdherence && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          )}

          {adherenceData && !loadingAdherence && (
            <>
              {/* Global stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckSquare className="w-4 h-4 text-indigo-500" />
                    <span className="text-xs text-slate-500">Observance globale</span>
                  </div>
                  <p className={`text-2xl font-bold ${adherenceData.global_adherence >= 70 ? "text-green-600" : adherenceData.global_adherence >= 40 ? "text-amber-600" : "text-red-600"}`}>
                    {adherenceData.global_adherence}%
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-slate-500">Patients suivis</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">{adherenceData.total_patients}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-teal-500" />
                    <span className="text-xs text-slate-500">Periode</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">{adherenceData.period_months} mois</p>
                </div>
              </div>

              {/* Patient list */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <h3 className="text-sm font-bold text-slate-800">Observance par patient</h3>
                  <p className="text-xs text-slate-500">Check-ins realises / attendus (1 par jour)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="adherence-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Patient</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Cancer</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Protocole</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Ligne</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Check-ins</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Jours</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Observance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adherenceData.patients.map(p => (
                        <tr key={p.patientId} className="border-b border-slate-50 hover:bg-slate-25">
                          <td className="px-4 py-2 font-medium text-slate-700">{p.firstName} {p.lastName}</td>
                          <td className="px-4 py-2 text-slate-500">{p.cancerType || "-"}</td>
                          <td className="px-4 py-2 text-slate-500 text-xs">{p.protocol || "-"}</td>
                          <td className="px-4 py-2 text-slate-500 text-xs">{p.therapeuticLine || "-"}</td>
                          <td className="px-4 py-2 text-center text-slate-600">{p.actualCheckins}</td>
                          <td className="px-4 py-2 text-center text-slate-400">{p.daysMonitored}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                              p.adherencePct >= 70 ? "bg-green-100 text-green-700" :
                              p.adherencePct >= 40 ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {p.adherencePct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Structured transmissions Tab */}
      {activeTab === "transmissions" && (
        <div className="space-y-4" data-testid="pharma-transmissions-tab">
          <div className="flex justify-end">
            <button
              onClick={fetchTransmissions}
              disabled={loadingTransmissions}
              data-testid="pharma-transmissions-refresh-button"
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {loadingTransmissions ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Actualiser
            </button>
          </div>

          {loadingTransmissions && (
            <div className="flex items-center justify-center py-12" data-testid="pharma-transmissions-loading">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          )}

          {transmissionData && !loadingTransmissions && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pharma-transmissions-kpis">
                <TransmissionKpi label="Événements" value={transmissionData.totals?.structuredTransmissionEvents || 0} testid="pharma-transmission-total" />
                <TransmissionKpi label="Liés traitement" value={transmissionData.totals?.treatmentRelated || 0} testid="pharma-transmission-treatment-related" />
                <TransmissionKpi label="Liés observance" value={transmissionData.totals?.oralAdherenceRelated || 0} testid="pharma-transmission-adherence-related" />
                <TransmissionKpi label="Avis médical" value={`${transmissionData.totals?.medicalReviewRatePct || 0}%`} testid="pharma-transmission-medical-review-rate" />
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <DistributionCard title="Par priorité" data={transmissionData.distributions?.byPriority} testid="pharma-transmission-by-priority" />
                <DistributionCard title="Actions attendues" data={transmissionData.distributions?.byActionRequired} testid="pharma-transmission-by-action" />
                <DistributionCard title="Par protocole" data={transmissionData.distributions?.byProtocol} testid="pharma-transmission-by-protocol" />
                <DistributionCard title="Par type de cancer" data={transmissionData.distributions?.byCancerType} testid="pharma-transmission-by-cancer" />
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="pharma-transmission-tags-card">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Tags cliniques</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <TagCloud title="Tags manuels" tags={transmissionData.distributions?.topManualTags} />
                  <TagCloud title="Tags suggérés" tags={transmissionData.distributions?.topSuggestedTags} />
                </div>
                <p className="mt-4 text-xs text-slate-500" data-testid="pharma-transmission-privacy-note">
                  Aucun contenu libre de transmission n’est inclus dans cette analyse.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Study Report */}
      {activeTab === "toxicity" && <ToxicityByCycleDay />}

      {activeTab === "report" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Parametres du rapport</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Type de cancer</label>
                <input
                  type="text"
                  value={reportCancer}
                  onChange={e => setReportCancer(e.target.value)}
                  placeholder="Ex: Poumon (vide = tous)"
                  data-testid="report-cancer-input"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Protocole</label>
                <input
                  type="text"
                  value={reportProtocol}
                  onChange={e => setReportProtocol(e.target.value)}
                  placeholder="Ex: Cisplatine (vide = tous)"
                  data-testid="report-protocol-input"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Periode (mois)</label>
                <select
                  value={reportMonths}
                  onChange={e => setReportMonths(Number(e.target.value))}
                  data-testid="report-months-select"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {[3, 6, 12, 18, 24].map(m => (
                    <option key={m} value={m}>{m} mois</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={fetchStudyReport}
                  disabled={loadingReport}
                  data-testid="generate-report-btn"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loadingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                  Generer
                </button>
                <button
                  onClick={downloadStudyPdf}
                  disabled={exportingPdf}
                  data-testid="download-report-pdf-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-50"
                >
                  {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  PDF
                </button>
              </div>
            </div>
          </div>

          {/* Study report results */}
          {studyReport && (
            <div className="space-y-4" data-testid="study-report-results">
              {/* Title + key metrics */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-base font-bold text-slate-800 mb-1">{studyReport.title}</h3>
                <p className="text-xs text-slate-400 mb-4">{studyReport.period}</p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-indigo-700">{studyReport.population?.n_patients}</p>
                    <p className="text-xs text-slate-500">Patients</p>
                  </div>
                  <div className="bg-teal-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-teal-700">{studyReport.follow_up?.total_checkins}</p>
                    <p className="text-xs text-slate-500">Check-ins</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-red-600">{studyReport.safety?.eig_rate_per_100pm}</p>
                    <p className="text-xs text-slate-500">EIG / 100 PM</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-orange-600">{studyReport.safety?.pct_critical}%</p>
                    <p className="text-xs text-slate-500">Alertes critiques</p>
                  </div>
                </div>
              </div>

              {/* Toxicity table */}
              {studyReport.safety?.toxicity_table?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                    <h4 className="text-sm font-bold text-slate-700">Profil de Toxicite (CTCAE v5.0)</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="toxicity-table">
                      <thead>
                        <tr className="bg-slate-800 text-white text-xs">
                          <th className="text-left px-4 py-2">Symptome CTCAE</th>
                          <th className="text-left px-4 py-2">MedDRA</th>
                          <th className="text-center px-4 py-2">N</th>
                          <th className="text-center px-4 py-2">Score moy</th>
                          <th className="text-center px-4 py-2">% tous grades</th>
                          <th className="text-center px-4 py-2">% severe (G2+)</th>
                          <th className="text-center px-4 py-2">% G3</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studyReport.safety.toxicity_table.map((t, i) => (
                          <tr key={i} className={`border-b border-slate-100 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                            <td className="px-4 py-2 font-medium text-slate-800">{t.ctcae_term || t.symptom}</td>
                            <td className="px-4 py-2 text-xs text-indigo-600 font-mono">{t.meddra_code}</td>
                            <td className="px-4 py-2 text-center text-slate-500">{t.n_observations}</td>
                            <td className="px-4 py-2 text-center">{t.mean_score}</td>
                            <td className="px-4 py-2 text-center">{t.pct_all_grade}%</td>
                            <td className="px-4 py-2 text-center">
                              <span className={`font-bold ${t.pct_severe > 30 ? "text-red-600" : t.pct_severe > 15 ? "text-orange-500" : "text-slate-600"}`}>
                                {t.pct_severe}%
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className={`font-bold ${t.pct_grade3 > 10 ? "text-red-600" : "text-slate-600"}`}>
                                {t.pct_grade3}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Vitals summary */}
              {studyReport.vitals?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                    <h4 className="text-sm font-bold text-slate-700">Constantes Vitales (LOINC)</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-emerald-600 text-white text-xs">
                          <th className="text-left px-4 py-2">Parametre</th>
                          <th className="text-left px-4 py-2">LOINC</th>
                          <th className="text-left px-4 py-2">Unite</th>
                          <th className="text-center px-4 py-2">N</th>
                          <th className="text-center px-4 py-2">Moyenne</th>
                          <th className="text-center px-4 py-2">Mediane</th>
                          <th className="text-center px-4 py-2">Min-Max</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studyReport.vitals.map((v, i) => (
                          <tr key={i} className={`border-b border-slate-100 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                            <td className="px-4 py-2 font-medium text-slate-800">{v.parameter}</td>
                            <td className="px-4 py-2 text-xs text-emerald-600 font-mono">{v.loinc_code}</td>
                            <td className="px-4 py-2 text-xs text-slate-500">{v.unit}</td>
                            <td className="px-4 py-2 text-center">{v.n}</td>
                            <td className="px-4 py-2 text-center">{v.mean}</td>
                            <td className="px-4 py-2 text-center">{v.median}</td>
                            <td className="px-4 py-2 text-center text-slate-400">{v.min}-{v.max}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TransmissionKpi({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}


function DistributionCard({ title, data, testid }) {
  const rows = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <h3 className="text-sm font-bold text-slate-800 mb-3">{title}</h3>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-xs text-slate-400">Aucune donnée</p>}
        {rows.map(([key, count]) => (
          <div key={key} className="flex items-center justify-between text-sm rounded-lg bg-slate-50 px-3 py-2">
            <span className="text-slate-600">{key}</span>
            <span className="font-semibold text-slate-800">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function TagCloud({ title, tags }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-2">{title}</p>
      <div className="flex flex-wrap gap-2">
        {(tags || []).length === 0 && <span className="text-xs text-slate-400">Aucune donnée</span>}
        {(tags || []).map(item => (
          <span key={item.key} className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
            {item.key} · {item.count}
          </span>
        ))}
      </div>
    </div>
  );
}
